/**
 * Local/in-memory transport for testing.
 *
 * Allows connecting multiple LocalTransport instances together
 * with simulated network conditions (latency, jitter, packet loss).
 */

import type { TransportAdapter } from "./adapter.js";

/**
 * Configuration for network simulation.
 */
export interface LocalTransportConfig {
	/**
	 * One-way latency in milliseconds.
	 * @default 0
	 */
	latency?: number;

	/**
	 * Random variation in latency (jitter) in milliseconds.
	 * @default 0
	 */
	jitter?: number;

	/**
	 * Probability of packet loss (0-1).
	 * @default 0
	 */
	packetLoss?: number;

	/**
	 * Whether to use deterministic mode (seeded random).
	 * @default false
	 */
	deterministic?: boolean;

	/**
	 * Seed for deterministic mode.
	 * @default 12345
	 */
	seed?: number;
}

/**
 * A pending message waiting to be delivered.
 */
interface PendingMessage {
	/** Target peer ID */
	targetPeerId: string;
	/** Message data */
	message: Uint8Array;
	/** Simulated time when this message should be delivered */
	deliverAt: number;
	/** Whether this is a reliable message */
	reliable: boolean;
}

/**
 * Min-heap implementation for efficient message queue ordering.
 * Uses deliverAt time as the priority.
 */
class MessageHeap {
	private readonly heap: PendingMessage[] = [];

	/**
	 * Get the number of messages in the heap.
	 */
	get length(): number {
		return this.heap.length;
	}

	/**
	 * Insert a message into the heap. O(log n)
	 */
	push(message: PendingMessage): void {
		this.heap.push(message);
		this.bubbleUp(this.heap.length - 1);
	}

	/**
	 * Remove and return the message with the earliest delivery time. O(log n)
	 */
	pop(): PendingMessage | undefined {
		if (this.heap.length === 0) {
			return undefined;
		}

		const min = this.heap[0];
		const last = this.heap.pop();

		if (this.heap.length > 0 && last !== undefined) {
			this.heap[0] = last;
			this.bubbleDown(0);
		}

		return min;
	}

	/**
	 * Peek at the message with the earliest delivery time without removing it.
	 */
	peek(): PendingMessage | undefined {
		return this.heap[0];
	}

	/**
	 * Clear all messages from the heap.
	 */
	clear(): void {
		this.heap.length = 0;
	}

	/**
	 * Move a node up the heap to maintain heap property.
	 */
	private bubbleUp(startIndex: number): void {
		let idx = startIndex;
		while (idx > 0) {
			const parentIndex = Math.floor((idx - 1) / 2);
			const parent = this.heap[parentIndex];
			const current = this.heap[idx];

			if (parent === undefined || current === undefined) break;
			if (parent.deliverAt <= current.deliverAt) break;

			// Swap with parent
			this.heap[parentIndex] = current;
			this.heap[idx] = parent;
			idx = parentIndex;
		}
	}

	/**
	 * Move a node down the heap to maintain heap property.
	 */
	private bubbleDown(startIndex: number): void {
		const length = this.heap.length;
		let idx = startIndex;

		while (true) {
			const leftIndex = 2 * idx + 1;
			const rightIndex = 2 * idx + 2;
			let smallest = idx;

			const current = this.heap[idx];
			const left = this.heap[leftIndex];
			const right = this.heap[rightIndex];

			if (current === undefined) break;

			if (
				leftIndex < length &&
				left !== undefined &&
				left.deliverAt < current.deliverAt
			) {
				smallest = leftIndex;
			}

			const smallestItem = this.heap[smallest];
			if (
				rightIndex < length &&
				right !== undefined &&
				smallestItem !== undefined &&
				right.deliverAt < smallestItem.deliverAt
			) {
				smallest = rightIndex;
			}

			if (smallest === idx) break;

			// Swap with smallest child
			const swap = this.heap[smallest];
			if (swap !== undefined) {
				this.heap[smallest] = current;
				this.heap[idx] = swap;
			}
			idx = smallest;
		}
	}
}

/**
 * Simple seeded random number generator for deterministic testing.
 */
class SeededRandom {
	private seed: number;

	constructor(seed: number) {
		this.seed = seed;
	}

	/**
	 * Returns a random number between 0 and 1.
	 */
	next(): number {
		// Simple LCG
		this.seed = (this.seed * 1664525 + 1013904223) >>> 0;
		return this.seed / 0xffffffff;
	}
}

/**
 * In-memory transport for testing.
 *
 * Features:
 * - Connect multiple LocalTransport instances together
 * - Simulated network conditions (latency, jitter, packet loss)
 * - Deterministic mode for reproducible tests
 * - Manual time control via tick() method
 */
export class LocalTransport implements TransportAdapter {
	public onMessage: ((peerId: string, message: Uint8Array) => void) | null =
		null;
	public onConnect: ((peerId: string) => void) | null = null;
	public onDisconnect: ((peerId: string) => void) | null = null;
	public onError:
		| ((peerId: string | null, error: Error, context: string) => void)
		| null = null;

	/** Callback for keepalive ping - set by Session to send Ping messages */
	public onKeepalivePing: ((peerId: string) => void) | null = null;

	private readonly _connectedPeers: Set<string> = new Set();
	private readonly linkedTransports: Map<string, LocalTransport> = new Map();
	private readonly pendingMessages: MessageHeap = new MessageHeap();
	private readonly packetLoss: number;
	private readonly random: SeededRandom | null;
	private currentTime = 0;
	/** Simulated one-way latency in milliseconds */
	private _latency = 0;
	/** Simulated jitter (latency variation) in milliseconds */
	private _jitter = 0;

	/**
	 * Create a new LocalTransport.
	 *
	 * @param localPeerId - Unique ID for this transport
	 * @param config - Network simulation configuration
	 */
	constructor(
		public readonly localPeerId: string,
		config?: LocalTransportConfig,
	) {
		this.latency = config?.latency ?? 0;
		this.jitter = config?.jitter ?? 0;
		this.packetLoss = config?.packetLoss ?? 0;

		const deterministic = config?.deterministic ?? false;
		const seed = config?.seed ?? 12345;
		this.random = deterministic ? new SeededRandom(seed) : null;
	}

	/**
	 * Get the set of connected peer IDs.
	 */
	get connectedPeers(): ReadonlySet<string> {
		return this._connectedPeers;
	}

	/**
	 * Link two LocalTransport instances together.
	 * Creates a bidirectional connection.
	 *
	 * @param a - First transport
	 * @param b - Second transport
	 */
	static link(a: LocalTransport, b: LocalTransport): void {
		a.linkedTransports.set(b.localPeerId, b);
		b.linkedTransports.set(a.localPeerId, a);
	}

	/**
	 * Unlink two LocalTransport instances.
	 *
	 * @param a - First transport
	 * @param b - Second transport
	 */
	static unlink(a: LocalTransport, b: LocalTransport): void {
		a.linkedTransports.delete(b.localPeerId);
		b.linkedTransports.delete(a.localPeerId);
	}

	/**
	 * Connect to a peer.
	 * The peer must be linked via LocalTransport.link().
	 */
	async connect(peerId: string): Promise<void> {
		const peer = this.linkedTransports.get(peerId);
		if (!peer) {
			throw new Error(`Peer ${peerId} is not linked`);
		}

		if (this._connectedPeers.has(peerId)) {
			return; // Already connected
		}

		// Add to connected peers on both sides
		this._connectedPeers.add(peerId);
		peer._connectedPeers.add(this.localPeerId);

		// Notify both sides
		this.onConnect?.(peerId);
		peer.onConnect?.(this.localPeerId);
	}

	/**
	 * Disconnect from a peer.
	 */
	disconnect(peerId: string): void {
		if (!this._connectedPeers.has(peerId)) {
			return;
		}

		const peer = this.linkedTransports.get(peerId);

		// Remove from connected peers on both sides
		this._connectedPeers.delete(peerId);
		if (peer) {
			peer._connectedPeers.delete(this.localPeerId);
			peer.onDisconnect?.(this.localPeerId);
		}

		this.onDisconnect?.(peerId);
	}

	/**
	 * Disconnect from all peers.
	 */
	disconnectAll(): void {
		const peers = Array.from(this._connectedPeers);
		for (const peerId of peers) {
			this.disconnect(peerId);
		}
	}

	/**
	 * Send a message to a specific peer.
	 * Uses a min-heap for O(log n) insertion instead of O(n log n) sort.
	 */
	send(peerId: string, message: Uint8Array, reliable: boolean): void {
		if (!this._connectedPeers.has(peerId)) {
			return; // Not connected, silently drop
		}

		// Check for packet loss (unreliable only)
		if (!reliable && this.shouldDropPacket()) {
			return;
		}

		// Calculate delivery time with latency and jitter
		const deliverAt = this.currentTime + this.calculateDelay();

		// Copy message to prevent external mutation
		const messageCopy = new Uint8Array(message.length);
		messageCopy.set(message);

		// Push to heap - O(log n) instead of O(n log n) for sort
		this.pendingMessages.push({
			targetPeerId: peerId,
			message: messageCopy,
			deliverAt,
			reliable,
		});
	}

	/**
	 * Broadcast a message to all connected peers.
	 */
	broadcast(message: Uint8Array, reliable: boolean): void {
		for (const peerId of this._connectedPeers) {
			this.send(peerId, message, reliable);
		}
	}

	/**
	 * Process all pending messages immediately, ignoring simulated delay.
	 * Useful for synchronous tests.
	 */
	flush(): void {
		while (this.pendingMessages.length > 0) {
			const pending = this.pendingMessages.pop();
			if (pending !== undefined) {
				this.deliverMessage(pending);
			}
		}
	}

	/**
	 * Advance simulated time and deliver any messages due.
	 *
	 * @param deltaMs - Time to advance in milliseconds
	 */
	tick(deltaMs: number): void {
		this.currentTime += deltaMs;
		this.deliverDueMessages();
	}

	/**
	 * Get the current simulated time.
	 */
	getCurrentTime(): number {
		return this.currentTime;
	}

	/**
	 * Set the current simulated time.
	 */
	setCurrentTime(time: number): void {
		this.currentTime = time;
		this.deliverDueMessages();
	}

	/**
	 * Get the number of pending messages.
	 */
	getPendingMessageCount(): number {
		return this.pendingMessages.length;
	}

	/**
	 * Deliver all messages that are due based on current time.
	 * Uses O(k log n) where k is the number of messages delivered.
	 */
	private deliverDueMessages(): void {
		while (this.pendingMessages.length > 0) {
			const first = this.pendingMessages.peek();
			if (first === undefined || first.deliverAt > this.currentTime) {
				break;
			}
			this.pendingMessages.pop();
			this.deliverMessage(first);
		}
	}

	/**
	 * Deliver a single message to its target peer.
	 * Messages are delivered regardless of current connection state, simulating
	 * real networks where messages in flight can arrive after disconnect.
	 */
	private deliverMessage(pending: PendingMessage): void {
		const peer = this.linkedTransports.get(pending.targetPeerId);
		if (peer) {
			peer.onMessage?.(this.localPeerId, pending.message);
		}
	}

	/**
	 * Calculate delay for a message based on latency and jitter.
	 */
	private calculateDelay(): number {
		let delay = this._latency;

		if (this._jitter > 0) {
			const jitterAmount = this.getRandomValue() * this._jitter * 2;
			delay += jitterAmount - this._jitter;
		}

		return Math.max(0, delay);
	}

	/**
	 * Determine if a packet should be dropped.
	 */
	private shouldDropPacket(): boolean {
		if (this.packetLoss <= 0) {
			return false;
		}
		return this.getRandomValue() < this.packetLoss;
	}

	/**
	 * Get a random value between 0 and 1.
	 */
	private getRandomValue(): number {
		if (this.random) {
			return this.random.next();
		}
		return Math.random();
	}

	/**
	 * Get the current simulated latency in milliseconds.
	 */
	get latency(): number {
		return this._latency;
	}

	/**
	 * Set the simulated latency in milliseconds.
	 * Can be changed at any time; affects only new messages.
	 */
	set latency(value: number) {
		this._latency = Math.max(0, value);
	}

	/**
	 * Get the current simulated jitter in milliseconds.
	 */
	get jitter(): number {
		return this._jitter;
	}

	/**
	 * Set the simulated jitter in milliseconds.
	 * Can be changed at any time; affects only new messages.
	 */
	set jitter(value: number) {
		this._jitter = Math.max(0, value);
	}

	/**
	 * Trigger keepalive pings for all connected peers.
	 * Call this periodically to trigger RTT measurement.
	 * The Session will handle the actual Ping/Pong and RTT calculation.
	 */
	triggerKeepalive(): void {
		if (!this.onKeepalivePing) return;
		for (const peerId of this._connectedPeers) {
			this.onKeepalivePing(peerId);
		}
	}
}

/**
 * Create a group of linked LocalTransport instances.
 *
 * @param peerIds - Array of peer IDs to create
 * @param config - Optional shared configuration
 * @returns Map of peer ID to LocalTransport
 */
export function createLocalTransportGroup(
	peerIds: string[],
	config?: LocalTransportConfig,
): Map<string, LocalTransport> {
	const transports = new Map<string, LocalTransport>();

	// Create all transports
	for (const peerId of peerIds) {
		transports.set(peerId, new LocalTransport(peerId, config));
	}

	// Link all transports together (full mesh)
	const transportArray = Array.from(transports.values());
	for (let i = 0; i < transportArray.length; i++) {
		const transportI = transportArray[i];
		if (!transportI) continue;
		for (let j = i + 1; j < transportArray.length; j++) {
			const transportJ = transportArray[j];
			if (!transportJ) continue;
			LocalTransport.link(transportI, transportJ);
		}
	}

	return transports;
}
